/**
 * Tenant admin — Customer Support contact.
 *
 * Tenants configure ONE of three help-button modes shown to customers
 * on the Order Track page:
 *   • WHATSAPP — deep-links to wa.me with an optional prefilled message
 *   • CALL     — deep-links to tel:
 *   • BOTH     — shows both buttons side-by-side, customer picks
 */
import { useEffect, useState } from "react";
import { Save, Loader2, MessageCircle, Phone } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = { kind: "WHATSAPP", whatsapp_number: "", call_number: "", number: "", message: "Hi, I need help with my order." };

export default function AdminSupportContact() {
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/support-contact");
            setForm({ ...EMPTY, ...data });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load support contact");
        } finally { setBusy(false); }
    };
    useEffect(() => { Promise.resolve().then(load); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                kind: form.kind,
                whatsapp_number: form.whatsapp_number || "",
                call_number: form.call_number || "",
                number: form.number || "",
                message: form.message || "Hi, I need help with my order.",
            };
            const { data } = await api.patch("/admin/support-contact", payload);
            setForm({ ...EMPTY, ...data });
            toast.success("Support contact saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    if (busy) return <div className="py-20 text-center"><Loader2 className="inline animate-spin" /></div>;

    const showWA = form.kind === "WHATSAPP" || form.kind === "BOTH";
    const showCall = form.kind === "CALL" || form.kind === "BOTH";

    return (
        <div className="max-w-2xl">
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-2xl font-bold">Customer Support</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        The Help button on every customer order will deep-link to your support channel.
                    </p>
                </div>
                <button onClick={save} disabled={saving} className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="support-save">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                </button>
            </div>

            <div className="bg-card rounded-2xl border border-border p-5 space-y-5">
                <div>
                    <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Preferred channel</label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                        {[
                            { value: "WHATSAPP", label: "WhatsApp only", Icon: MessageCircle },
                            { value: "CALL", label: "Call only", Icon: Phone },
                            { value: "BOTH", label: "Both", Icon: MessageCircle },
                        ].map(({ value, label, Icon }) => (
                            <button
                                key={value}
                                onClick={() => setForm({ ...form, kind: value })}
                                data-testid={`support-kind-${value}`}
                                className={`rounded-xl border p-3 text-xs font-semibold inline-flex flex-col items-center gap-1 transition ${form.kind === value ? "border-primary bg-primary/10 text-primary" : "border-border bg-muted/40 hover:bg-muted"}`}
                            >
                                <Icon size={16} />
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {showWA && (
                    <div>
                        <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">WhatsApp number (with country code)</label>
                        <input
                            data-testid="support-whatsapp-number"
                            value={form.whatsapp_number}
                            onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                            placeholder="+919876543210"
                            className="mt-1 inp w-full"
                        />
                        <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mt-3 block">Default WhatsApp message</label>
                        <textarea
                            data-testid="support-message"
                            rows={2}
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            placeholder="Hi, I need help with my order."
                            className="mt-1 inp w-full"
                        />
                    </div>
                )}

                {showCall && (
                    <div>
                        <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Call number</label>
                        <input
                            data-testid="support-call-number"
                            value={form.call_number}
                            onChange={(e) => setForm({ ...form, call_number: e.target.value })}
                            placeholder="+919876543210"
                            className="mt-1 inp w-full"
                        />
                    </div>
                )}

                <div className="rounded-xl bg-muted/40 border border-border p-3 text-xs text-muted-foreground" data-testid="support-preview">
                    <p className="font-bold text-foreground mb-1">Preview</p>
                    Customers tapping &quot;Need help?&quot; will see {form.kind === "BOTH" ? "both WhatsApp and Call buttons" : form.kind === "CALL" ? "a Call button" : "a WhatsApp button"}.
                </div>
            </div>

            <style>{`.inp{background:hsl(var(--muted));border:1px solid hsl(var(--border));border-radius:0.75rem;padding:0.5rem 0.75rem;font-size:0.875rem;outline:none}.inp:focus{box-shadow:0 0 0 3px hsl(var(--primary) / 0.2)}`}</style>
        </div>
    );
}
